/**
 * Post-payment tour — step definitions.
 *
 * Triggered the first time a user lands on `/dashboard` after a successful
 * Stripe checkout (or via the Replay tile in /tutorials). Walks the user
 * through the two paid-only modules — better/notes (5 steps) and the
 * Integrations hub (3 steps) — bookended by a welcome and finish scene.
 *
 * Differs from FirstLoginTour in one way: each step has a `routePath`,
 * because the tour spans 4 different pages. The component listens to
 * the current route via `useLocation` and only renders the spotlight
 * when the user is on the matching page; the inter-route transition
 * is handled by the `navigatesNext` flag, which makes the step's
 * primary CTA push the next step's `routePath` to the router before
 * incrementing the index.
 *
 * Copy lives under the `post_payment` namespace in `src/locales/{fr,en}/tour.json`.
 */
export type PostPaymentTourStepMode = 'fullscreen' | 'spotlight'

export interface PostPaymentTourStep {
  /** Stable id — also used as i18n key (`post_payment.<id>.{title,body,...}`). */
  id: string
  /** Rendering mode: fullscreen scene or spotlight on a target. */
  mode: PostPaymentTourStepMode
  /** Route the user must be on for this step to render. The component
   *  shows a small "Open the next module" intermediate card if the user
   *  is on a different route (e.g. they hit Back in the browser). */
  routePath: string
  /** CSS selector of the element to highlight. Required for spotlight mode. */
  targetSelector?: string
  /** When true, clicking the step's primary CTA navigates the router to
   *  the NEXT step's `routePath` before incrementing the index. Used to
   *  bridge route transitions cleanly. */
  navigatesNext?: boolean
}

export const POST_PAYMENT_TOUR_STEPS: PostPaymentTourStep[] = [
  // ── Dashboard ──────────────────────────────────────────────────────
  { id: 'welcome', mode: 'fullscreen', routePath: '/dashboard' },
  {
    id: 'betternotes_nav',
    mode: 'spotlight',
    routePath: '/dashboard',
    targetSelector: '[data-tour="nav-betternotes"]',
    navigatesNext: true,
  },
  // ── better/notes ───────────────────────────────────────────────────
  // Library is the index route at `/betternotes`. We spotlight the
  // SIDEBAR nav items (small, stable elements) rather than the page
  // content wrappers — Mustapha's live test showed the wrappers occupy
  // ~80% of the viewport, leaving no space for the TourCard to anchor
  // above or below, so the card landed off-screen.
  //
  // App.tsx routes:
  //   /betternotes (index) → BetterNotesLibrary
  //   /betternotes/upcoming → BetterNotesUpcoming
  //   /betternotes/past → BetterNotesPast
  //   /betternotes/settings → BetterNotesSettings
  {
    id: 'library',
    mode: 'spotlight',
    routePath: '/betternotes',
    targetSelector: '[data-tour="bn-sidebar-library"]',
    navigatesNext: true,
  },
  {
    id: 'upcoming',
    mode: 'spotlight',
    routePath: '/betternotes/upcoming',
    targetSelector: '[data-tour="bn-sidebar-upcoming"]',
    navigatesNext: true,
  },
  {
    id: 'past',
    mode: 'spotlight',
    routePath: '/betternotes/past',
    targetSelector: '[data-tour="bn-sidebar-past"]',
    navigatesNext: true,
  },
  // ── better/notes settings (4 spotlights, same page) ────────────────
  {
    id: 'settings_calendar',
    mode: 'spotlight',
    routePath: '/betternotes/settings',
    targetSelector: '[data-tour="bn-settings-calendar"]',
  },
  {
    id: 'settings_record_mode',
    mode: 'spotlight',
    routePath: '/betternotes/settings',
    targetSelector: '[data-tour="bn-settings-record-mode"]',
  },
  {
    id: 'settings_notif',
    mode: 'spotlight',
    routePath: '/betternotes/settings',
    targetSelector: '[data-tour="bn-settings-notif"]',
  },
  {
    id: 'settings_language',
    mode: 'spotlight',
    routePath: '/betternotes/settings',
    targetSelector: '[data-tour="bn-settings-language"]',
    navigatesNext: true,
  },
  // ── Transition: BetterNotes done, Integrations starting ────────────
  // Fullscreen scene that frames the second half of the tour. Lives on
  // /integrations so the route swap happens BEFORE this card shows;
  // the user reads the framing while standing on the new page, then
  // the spotlights kick in.
  { id: 'transition_integrations', mode: 'fullscreen', routePath: '/integrations' },
  // ── Integrations (7 spotlights, same page) ─────────────────────────
  {
    id: 'int_telegram',
    mode: 'spotlight',
    routePath: '/integrations',
    targetSelector: '[data-tour="int-telegram"]',
  },
  {
    id: 'int_slack',
    mode: 'spotlight',
    routePath: '/integrations',
    targetSelector: '[data-tour="int-slack"]',
  },
  {
    id: 'int_briefing',
    mode: 'spotlight',
    routePath: '/integrations',
    targetSelector: '[data-tour="int-briefing"]',
  },
  {
    id: 'int_eod',
    mode: 'spotlight',
    routePath: '/integrations',
    targetSelector: '[data-tour="int-eod"]',
  },
  {
    id: 'int_weekly',
    mode: 'spotlight',
    routePath: '/integrations',
    targetSelector: '[data-tour="int-weekly"]',
  },
  {
    id: 'int_timezone',
    mode: 'spotlight',
    routePath: '/integrations',
    targetSelector: '[data-tour="int-timezone"]',
  },
  {
    id: 'int_api',
    mode: 'spotlight',
    routePath: '/integrations',
    targetSelector: '[data-tour="int-api"]',
  },
  // ── Finish ─────────────────────────────────────────────────────────
  { id: 'finish', mode: 'fullscreen', routePath: '/integrations' },
]
